/**
 * Cancha Page - Shows active game or invites to scan QR
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Clock, Zap, CheckCircle2, Send,
  MapPin, ChevronRight, DoorOpen, QrCode, Scan
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { WeatherWidget } from '../components/WeatherWidget';
import { Navbar } from '../components/Navbar';
import { clipsAPI, courtAPI } from '../services/api';
import { useAuthStore, useSessionStore, useLocationStore, useClipsStore } from '../store';
import { toast } from 'sonner';
import { getRandomPhrase } from '../utils/replayPhrases';

export default function Cancha() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { club, court, expiresAt, clearSession, setSession } = useSessionStore();
  const { userLocation } = useLocationStore();
  
  const [requestSent, setRequestSent] = useState(null); // 'normal' | 'fast' | null
  const [sentPhrase, setSentPhrase] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isValidatingSession, setIsValidatingSession] = useState(true);
  
  // Verificar sesión con backend al cargar
  useEffect(() => {
    const validateSession = async () => {
      setIsValidatingSession(true);
      try {
        const response = await courtAPI.getSession();
        const serverSession = response.data;
        
        if (serverSession && serverSession.active) {
          // Sesión válida en el servidor
          setSessionInfo(serverSession);
          // Sincronizar con store local si es necesario
          if (!club || !court) {
            setSession({
              club: serverSession.club || club,
              court: serverSession.court || court,
              expires_at: serverSession.expires_at || expiresAt,
            });
          }
        } else {
          // No hay sesión activa en el servidor - limpiar store local
          if (club || court) {
            clearSession();
          }
          setSessionInfo({ active: false });
        }
      } catch (err) {
        console.error('Session validation error:', err);
        // En caso de error, limpiar sesión local para evitar inconsistencias
        if (club || court) {
          clearSession();
        }
        setSessionInfo({ active: false });
      } finally {
        setIsValidatingSession(false);
      }
    };
    
    validateSession();
  }, []); // Only on mount - session validation happens once
  
  // Determinar si hay sesión activa después de validar
  const hasActiveSession = !isValidatingSession && !!(club && court) && sessionInfo?.active !== false;
  
  // Clear confirmation after 5 seconds
  useEffect(() => {
    if (requestSent) {
      const timer = setTimeout(() => setRequestSent(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [requestSent]);
  
  const handleRequestClip = async (isFast = false) => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
    
    // Show immediate confirmation with random phrase
    setRequestSent(isFast ? 'fast' : 'normal');
    setSentPhrase(getRandomPhrase());
    
    // Duration: Normal = 30s, Fast = 60s (will become 30s at 2x)
    const captureDuration = isFast ? 60 : 30;
    
    try {
      await clipsAPI.requestClip({
        session_id: sessionInfo?.court_id || court?.id || 'demo',
        duration: captureDuration,
        is_fast: isFast,
      });
      
      // Success toast with navigation hint
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">¡Solicitud enviada!</span>
          <span className="text-sm opacity-80">
            Tu replay estará listo en &quot;Mis Replays&quot; en menos de 5 min
          </span>
        </div>,
        { duration: 4000 }
      );
      
    } catch (err) {
      setRequestSent(null);
      const message = err.response?.data?.detail || 'Error solicitando replay';
      toast.error(message);
    }
  };
  
  const handleLeaveCourt = async () => {
    try {
      await courtAPI.leaveCourt();
      clearSession();
      toast.success('Saliste de la cancha');
    } catch (err) {
      console.error('Leave court error:', err);
      toast.error('Error al salir de la cancha');
    }
  };
  
  // Get location for weather
  const weatherLat = userLocation?.lat || club?.location?.lat || 19.4326;
  const weatherLng = userLocation?.lng || club?.location?.lng || -99.1332;
  
  // Show loading while validating session
  if (isValidatingSession) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }
  
  // If no active session, show invitation to scan
  if (!hasActiveSession) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-sm"
          >
            {/* Illustration */}
            <div className="w-32 h-32 rounded-full bg-card border-2 border-dashed border-border flex items-center justify-center mx-auto mb-8">
              <div className="relative">
                <QrCode className="w-16 h-16 text-muted-foreground" />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <Scan className="w-4 h-4 text-primary" />
                </div>
              </div>
            </div>
            
            <h2 className="font-display font-bold text-2xl md:text-3xl mb-3">
              No hay cancha activa
            </h2>
            
            <p className="text-muted-foreground mb-8">
              Escanea el código QR de una cancha para comenzar a jugar y grabar tus mejores jugadas
            </p>
            
            <Button
              className="w-full h-14 text-lg font-bold uppercase tracking-wider glow-primary"
              onClick={() => navigate('/scan')}
              data-testid="scan-qr-btn"
            >
              <QrCode className="w-5 h-5 mr-2" />
              Escanear código QR
            </Button>
            
            <p className="text-xs text-muted-foreground mt-6">
              Busca el código QR en la cancha de tu club
            </p>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // Active session view
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      {/* Weather widget with club/court info integrated */}
      <div className="px-4 py-3">
        <WeatherWidget 
          lat={weatherLat} 
          lng={weatherLng} 
          clubName={club?.name || 'Demo Club'}
          courtNumber={court?.court_number || 1}
        />
      </div>
      
      {/* Main action buttons */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        {/* 30s Normal button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => handleRequestClip(false)}
          disabled={requestSent === 'normal'}
          className="action-button w-full max-w-sm h-32 rounded-2xl bg-card border-2 border-primary/20 flex flex-col items-center justify-center gap-2 disabled:opacity-70 transition-all"
          data-testid="replay-30s-btn"
        >
          <AnimatePresence mode="wait">
            {requestSent === 'normal' ? (
              <motion.div
                key="sent"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Send className="w-6 h-6 text-green-500" />
                </div>
                <span className="text-sm text-green-500 font-medium text-center px-4">{sentPhrase}</span>
              </motion.div>
            ) : (
              <motion.div
                key="normal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-8 h-8 text-primary" />
                  <span className="font-display font-black text-4xl tracking-tighter">30s</span>
                </div>
                <span className="text-sm text-muted-foreground uppercase tracking-wider">
                  Replay Normal
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
        
        {/* 60s Fast button (outputs 30s at 2x speed) */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => handleRequestClip(true)}
          disabled={requestSent === 'fast'}
          className="action-button w-full max-w-sm h-32 rounded-2xl bg-primary/5 border-2 border-primary/30 flex flex-col items-center justify-center gap-2 disabled:opacity-70 glow-primary transition-all"
          data-testid="replay-60s-btn"
        >
          <AnimatePresence mode="wait">
            {requestSent === 'fast' ? (
              <motion.div
                key="sent"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Send className="w-6 h-6 text-green-500" />
                </div>
                <span className="text-sm text-green-500 font-medium text-center px-4">{sentPhrase}</span>
              </motion.div>
            ) : (
              <motion.div
                key="fast"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-8 h-8 text-primary" />
                  <span className="font-display font-black text-4xl tracking-tighter text-primary">60s</span>
                </div>
                <span className="text-sm text-primary/80 uppercase tracking-wider">
                  Replay Rápido 2x → 30s
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
      
      {/* Session info footer */}
      <div className="p-4 border-t border-border space-y-3">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{club?.city || 'México'}</span>
          </div>
          <span>
            Cancha activa hasta{' '}
            {expiresAt ? new Date(expiresAt).toLocaleTimeString('es-MX', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }) : '—'}
          </span>
        </div>
        
        {/* Leave court button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleLeaveCourt}
          data-testid="leave-court-btn"
        >
          <DoorOpen className="w-4 h-4 mr-2" />
          Salir de esta cancha
        </Button>
      </div>
    </div>
  );
}
